import * as React from 'react';
import axios from 'axios';
import ACTIONS from '../actions';
import { API_BASE_URL } from '../../config';
import useNotification from '../notification/notificationContext';
import { NOTIFICATIONS } from '../../config';


export const UsersContext = React.createContext();

function useUsers() {
  const context = React.useContext(UsersContext);

  if (!context) throw new Error('useUsers must be used within a UsersProvider.');

  const [usersState, dispatch] = context;

  const { setNotification } = useNotification();

  const token = localStorage.getItem('jwtToken');
  if (token) axios.defaults.headers.common['x-access-token'] = token; 
  
  const getPartnerInfo = (id) => {
    return axios.get(`${API_BASE_URL}/partner-info/get-partnerinfo/${id}`).catch((err) => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to get partner details.'
      })
      throw err;
    })
  }

  const getAllUsers = async () => {
    return axios.get(`${API_BASE_URL}/user/get-all`).then(res => {
      const response = res.data;
      dispatch({
        type: ACTIONS.GET_ALL_USERS,
        payload: response.data
      });
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to retrieve the list of users'
      })
      throw err;
    })
  }

  const getAllPartners = () => {
    return axios.get(`${API_BASE_URL}/partner-info/get-all-partners`).then(res => {
      const response = res.data;
      dispatch({
        type: ACTIONS.GET_ALL_PARTNERS,
        payload: response.data
      });
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to retrieve the list of partners'
      })
      throw err;
    })
  }

  const invitePartner = (data) => {
    return axios.post(`${API_BASE_URL}/user/invite-partner`, data).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'New partner awas successfully created'
      });
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Request failed",
        message: err.response?.data?.message || "An error occurred",
      });
      throw err;
    })
  }

  //TODO: ADD INVITE PATIENT
  const addPatient = (data) => {
    return axios.post(`${API_BASE_URL}/user/add-patient`, { ...data }).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'New patient successfully created'
      });
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Request failed",
        message: err.response?.data?.message || "An error occurred",
      });
      throw err;
    })
  }

  const updatePartnerInfo = (partnerInfoId, data) => {
    return axios.patch(`${API_BASE_URL}/partner-info/edit/${partnerInfoId}`, data).then(res => {
      dispatch({
        type: ACTIONS.UPDATE_PARTNER_INFO_COMPLETE,
        payload: res.data.data
      })
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Partner info was edited successfully'
      });
      return res.data.data
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Request failed",
        message: err.response?.data?.message || "An error occurred",
      });
      throw err;
    })
  }

  const getClinicPartners = (clinicId) => {
    return axios.get(`${API_BASE_URL}/partner-info/get-clinic-partners/${clinicId}`).then(res => {
      dispatch({
        type: ACTIONS.GET_ALL_CLINIC_PARTNERS,
        payload: res.data.data
      })
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Request failed",
        message: err.response?.data?.message || "An error occurred",
      });
    })
  }

  const getClinicDentists = (clinicId) => {
    return axios.get(`${API_BASE_URL}/partner-info/get-clinic-dentists-with-calendly/${clinicId}`).then(res => {
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Request failed",
        message: err.response?.data?.message || "An error occurred",
      });
      throw err;
    })
  }

  const getAllStaff = () => {
    return axios.get(`${API_BASE_URL}/user/get-all-staff`).then(res => {
      const response = res.data;
      dispatch({
        type: ACTIONS.GET_ALL_STAFF,
        payload: response.data
      });
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'We were unable to retrieve the list of staff'
      })
      throw err;
    })
  }

  const inviteStaff = (data) => {
    return axios.post(`${API_BASE_URL}/user/invite-staff`, data).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'An invitation was successfully sent to the staff'
      });
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data?.message || 'We were unable to invite the user'
      })
      throw err;
    })
  }  

  const getUserDetails = (userId) => {
    return axios.get(`${API_BASE_URL}/user/get-details/${userId}`).then((res) => {
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data?.message || 'We were unable to retrieve user details'
      })
      throw err;
    })
  }

    //update user info
  const updateUserById = (id, data) => {
    return axios.patch(`${API_BASE_URL}/user/update-user-by-id/${id}`, data).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: res.data.message || 'Successfully updated user details.'
      });
      return res.data;
    }).catch((err) => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }

  
  const deleteUser = (id) => {
    return axios.delete(`${API_BASE_URL}/user/delete/${id}`).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: res.data.message || 'Successfully deleted user.'
      });
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }
  
  const forceDeleteUser = (id) => {
    return axios.delete(`${API_BASE_URL}/user/force-delete/${id}`).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: res.data.message || 'Successfully erased user from the database.'
      });
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }

  //TODO: ADD ACTIVATE ROUTE
  const activateUser = (id) => {
    return axios.patch(`${API_BASE_URL}/user/activate/${id}`).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: res.data.message || 'Successfully activated user.'
      });
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }


  const addPatientInfo = (data) => {
    return axios.post(`${API_BASE_URL}/patient-info`, data).then(res => {
      return res.data;   
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
    })
  }

  const getAllPatients = async () => {
    return axios.get(`${API_BASE_URL}/user/get-all-patients`).then(res => {
      if (res.status === 200) return res;
      return;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
       title: 'Server error',
        message: 'We were unable to retrieve the list of patients'
      })
    })
  }
   

  const getPatientInfo = () => {
    return axios.get(`${API_BASE_URL}/patient-info`).then(res => {
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
    })
  }

  const updatePatientInfo = (data) => {
    return axios.patch(`${API_BASE_URL}/patient-info/edit`, data).then(res => {
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });

    })
  }

  return {
    usersState,
    dispatch,
    getAllUsers,
    getAllPartners,
    getClinicDentists,
    invitePartner,
    updatePartnerInfo,
    getClinicPartners,
    getAllStaff,
    inviteStaff,
    getUserDetails,
    updateUserById,
    addPatient,
    getPartnerInfo,
    deleteUser,
    forceDeleteUser,
    activateUser,
    addPatientInfo,
    getPatientInfo,
    updatePatientInfo,
    getAllPatients,
  }
}

export default useUsers;

